import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { TableVisual } from '@/components/admin/TableVisual';
import { AddTableModal } from '@/components/admin/AddTableModal';

interface TableDoc {
    id: string;
    name: string;
    capacity: number;
    shape: 'round' | 'rectangle' | 'long';
    sortOrder: number;
    seatOrder?: string[];
}

interface GuestRow {
    id: string;
    fullName: string;
    tableNumber: string;
    plusOneName?: string;
    plusOneAllowed?: boolean;
    plusChildren?: boolean;
    children?: number;
    childrenNames?: string[];
    dietary?: any;
    status?: any;
    events?: string[];
    _guestType: string;
    _displayName: string;
}

function expandGuestsForSeating(guests: any[]): GuestRow[] {
    const expanded: GuestRow[] = [];
    const childrenGeneratedFor = new Set<string>();

    for (const g of guests) {
        expanded.push({
            ...g,
            _guestType: 'primary',
            _displayName: g.fullName,
        });

        if (g.plusOneName) {
            expanded.push({
                ...g,
                id: `${g.id}_plusOne`,
                _guestType: 'plusOne',
                _displayName: g.plusOneName,
                _parentId: g.id,
            });
        }

        const kidsCount = typeof g.children === 'number' ? g.children : 0;
        if (g.plusChildren && kidsCount > 0 && !childrenGeneratedFor.has(g.inviteCodeHash)) {
            childrenGeneratedFor.add(g.inviteCodeHash);
            const childNames: string[] = g.childrenNames || [];
            for (let i = 0; i < kidsCount; i++) {
                expanded.push({
                    ...g,
                    id: `${g.id}_child_${i}`,
                    _guestType: 'child',
                    _displayName: childNames[i] || `Child ${i + 1}`,
                    _parentId: g.id,
                });
            }
        }
    }

    return expanded;
}

export const TablePlanViewer: React.FC = () => {
    const [tables, setTables] = useState<TableDoc[]>([]);
    const [guests, setGuests] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Modal states
    const [isAddTableOpen, setIsAddTableOpen] = useState(false);
    const [editingTable, setEditingTable] = useState<TableDoc | null>(null);
    const [detailTableId, setDetailTableId] = useState<string | null>(null);

    // Assignment mode
    const [assigningGuest, setAssigningGuest] = useState<GuestRow | null>(null);

    // Sidebar
    const [sidebarOpen, setSidebarOpen] = useState(true);

    // Detail modal: add-guest search
    const [detailAddSearch, setDetailAddSearch] = useState('');

    // Drag-and-drop reorder state
    const [draggingPid, setDraggingPid] = useState<string | null>(null);
    const [dragOverPid, setDragOverPid] = useState<string | null>(null);
    const [sidebarSearch, setSidebarSearch] = useState('');

    const fetchData = async () => {
        setLoading(true);
        const [tablesSn, guestsSn] = await Promise.all([
            getDocs(collection(db, 'tables')),
            getDocs(collection(db, 'guests')),
        ]);
        setTables(
            tablesSn.docs
                .map(d => ({ id: d.id, ...d.data() } as TableDoc))
                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        );
        setGuests(guestsSn.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const esAttendingGuests = useMemo(() => guests.filter(g => g.status?.es !== 'no'), [guests]);
    const expandedGuests = useMemo(() => expandGuestsForSeating(esAttendingGuests), [esAttendingGuests]);

    // Sort expanded guests by seatOrder for a given table
    const sortBySeatOrder = (guests: { name: string; type: string; primaryId?: string }[], table: TableDoc | undefined) => {
        if (!table?.seatOrder || table.seatOrder.length === 0) return guests;
        const order = table.seatOrder;
        return [...guests].sort((a, b) => {
            const ai = a.primaryId ? order.indexOf(a.primaryId) : -1;
            const bi = b.primaryId ? order.indexOf(b.primaryId) : -1;
            if (ai === -1 && bi === -1) return 0;
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
        });
    };

    // Group guests by table name
    const guestsByTable = useMemo(() => {
        const map: Record<string, { name: string; type: string; primaryId?: string }[]> = {};
        for (const g of expandedGuests) {
            if (g.tableNumber) {
                if (!map[g.tableNumber]) map[g.tableNumber] = [];
                const primaryId = (g as any)._parentId || g.id;
                map[g.tableNumber].push({ name: g._displayName, type: g._guestType, primaryId });
            }
        }
        // Sort each table's guests by seatOrder
        for (const tableName of Object.keys(map)) {
            const table = tables.find(t => t.name === tableName);
            map[tableName] = sortBySeatOrder(map[tableName], table);
        }
        return map;
    }, [expandedGuests, tables]);

    // Unassigned = primary guests (and their expanded +1s/children) where tableNumber is empty
    const unassignedGuests = useMemo(() => {
        return expandedGuests.filter(g => !g.tableNumber);
    }, [expandedGuests]);

    const filteredUnassigned = useMemo(() => {
        if (!sidebarSearch) return unassignedGuests;
        const q = sidebarSearch.toLowerCase();
        return unassignedGuests.filter(g => g._displayName.toLowerCase().includes(q));
    }, [unassignedGuests, sidebarSearch]);

    // Find table names on guests that don't have a matching table doc
    const orphanedTableNames = useMemo(() => {
        const tableNames = new Set(tables.map(t => t.name));
        const guestTableNames = new Set(
            guests.map(g => g.tableNumber).filter(Boolean)
        );
        return [...guestTableNames].filter(n => !tableNames.has(n));
    }, [tables, guests]);

    // Stats
    const totalSeated = expandedGuests.filter(g => g.tableNumber).length;
    const totalCapacity = tables.reduce((s, t) => s + t.capacity, 0);

    // Assign a guest to a table (and append to seatOrder)
    const handleAssignGuest = async (tableName: string, guestOverride?: GuestRow) => {
        const guestToAssign = guestOverride || assigningGuest;
        if (!guestToAssign) return;

        // Find the primary guest doc for this person
        const primaryId = (guestToAssign as any)._parentId || guestToAssign.id;
        const primaryGuest = guests.find(g => g.id === primaryId);
        if (!primaryGuest) return;

        // Find the table doc to update seatOrder
        const table = tables.find(t => t.name === tableName);

        try {
            await updateDoc(doc(db, 'guests', primaryId), {
                tableNumber: tableName,
                updatedAt: new Date(),
            });
            // Append to seatOrder if table doc exists
            if (table) {
                const currentOrder = table.seatOrder || [];
                if (!currentOrder.includes(primaryId)) {
                    await updateDoc(doc(db, 'tables', table.id), {
                        seatOrder: [...currentOrder, primaryId],
                        updatedAt: new Date(),
                    });
                }
            }
            if (!guestOverride) setAssigningGuest(null);
            fetchData();
        } catch (err) {
            console.error('Failed to assign guest:', err);
            alert('Failed to assign guest to table.');
        }
    };

    // Remove a guest from a table (and remove from seatOrder)
    const handleRemoveFromTable = async (guestId: string) => {
        // Find which table this guest is on
        const guest = guests.find(g => g.id === guestId);
        const table = guest ? tables.find(t => t.name === guest.tableNumber) : null;

        try {
            await updateDoc(doc(db, 'guests', guestId), {
                tableNumber: '',
                updatedAt: new Date(),
            });
            // Remove from seatOrder
            if (table && table.seatOrder) {
                await updateDoc(doc(db, 'tables', table.id), {
                    seatOrder: table.seatOrder.filter(id => id !== guestId),
                    updatedAt: new Date(),
                });
            }
            fetchData();
        } catch (err) {
            console.error('Failed to remove guest:', err);
        }
    };

    // Reorder a guest within a table's seatOrder
    const handleReorderGuest = async (tableId: string, primaryId: string, direction: 'up' | 'down') => {
        const table = tables.find(t => t.id === tableId);
        if (!table) return;

        const order = buildSeatOrder(table);

        const idx = order.indexOf(primaryId);
        if (idx === -1) return;

        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= order.length) return;

        // Swap
        [order[idx], order[swapIdx]] = [order[swapIdx], order[idx]];

        try {
            await updateDoc(doc(db, 'tables', tableId), {
                seatOrder: order,
                updatedAt: new Date(),
            });
            fetchData();
        } catch (err) {
            console.error('Failed to reorder guest:', err);
        }
    };

    // Drag-and-drop reorder: move a guest from one position to another
    const handleDragReorder = async (tableId: string, fromPid: string, toPid: string) => {
        if (fromPid === toPid) return;
        const table = tables.find(t => t.id === tableId);
        if (!table) return;

        const order = buildSeatOrder(table);

        const fromIdx = order.indexOf(fromPid);
        const toIdx = order.indexOf(toPid);
        if (fromIdx === -1 || toIdx === -1) return;

        // Remove from old position, insert at new position
        order.splice(fromIdx, 1);
        order.splice(toIdx, 0, fromPid);

        try {
            await updateDoc(doc(db, 'tables', tableId), {
                seatOrder: order,
                updatedAt: new Date(),
            });
            fetchData();
        } catch (err) {
            console.error('Failed to drag-reorder guest:', err);
        }
    };

    // Build seatOrder from current guests if empty/stale
    const buildSeatOrder = (table: TableDoc): string[] => {
        let order = [...(table.seatOrder || [])];
        // Check if order covers current guests
        const currentPrimaryIds: string[] = [];
        const seen = new Set<string>();
        for (const g of expandedGuests.filter(eg => eg.tableNumber === table.name)) {
            const pid = (g as any)._parentId || g.id;
            if (!seen.has(pid)) {
                seen.add(pid);
                currentPrimaryIds.push(pid);
            }
        }
        // If order is empty or missing any current guests, rebuild
        if (order.length === 0 || currentPrimaryIds.some(pid => !order.includes(pid))) {
            order = currentPrimaryIds;
        }
        return order;
    };

    // Auto-create tables from existing tableNumber values
    const handleAutoCreate = async () => {
        if (orphanedTableNames.length === 0) return;
        if (!window.confirm(`Create ${orphanedTableNames.length} table(s) from existing assignments? (${orphanedTableNames.join(', ')})`)) return;

        const batch = writeBatch(db);
        orphanedTableNames.forEach((name, i) => {
            const ref = doc(collection(db, 'tables'));
            batch.set(ref, {
                name,
                capacity: 10,
                shape: 'round',
                sortOrder: tables.length + i,
                seatOrder: [],
                createdAt: new Date(),
                updatedAt: new Date(),
            });
        });
        await batch.commit();
        fetchData();
    };

    // Handle table click — if assigning, assign to this table; otherwise open detail
    const handleTableClick = (table: TableDoc) => {
        if (assigningGuest) {
            handleAssignGuest(table.name);
        } else {
            setDetailTableId(table.id);
        }
    };

    // Derive detailTable from tables array so it stays fresh after fetchData
    const detailTable = detailTableId ? tables.find(t => t.id === detailTableId) || null : null;

    // Get guests for detail modal — sorted by seatOrder
    const detailGuests = useMemo(() => {
        if (!detailTable) return [];
        const tableGuests = expandedGuests.filter(g => g.tableNumber === detailTable.name);
        if (!detailTable.seatOrder || detailTable.seatOrder.length === 0) return tableGuests;
        const order = detailTable.seatOrder;
        return [...tableGuests].sort((a, b) => {
            const aId = (a as any)._parentId || a.id;
            const bId = (b as any)._parentId || b.id;
            const ai = order.indexOf(aId);
            const bi = order.indexOf(bId);
            if (ai === -1 && bi === -1) return 0;
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
        });
    }, [detailTable, expandedGuests]);

    // Unassigned guests available to add from detail modal
    const detailAddCandidates = useMemo(() => {
        if (!detailTable || !detailAddSearch) return [];
        const q = detailAddSearch.toLowerCase();
        return unassignedGuests
            .filter(g => g._guestType === 'primary') // Only show primary guests for adding
            .filter(g => g._displayName.toLowerCase().includes(q))
            .slice(0, 8);
    }, [detailTable, detailAddSearch, unassignedGuests]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-32 animate-fade-in">
                <div className="text-center">
                    <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-secondary-light text-sm font-bold uppercase tracking-widest">Loading table plan…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in">
            {/* Header */}
            <div className="flex justify-between items-end flex-wrap gap-6 mb-8">
                <div className="space-y-1">
                    <p className="eyebrow">Seating</p>
                    <div className="flex items-center gap-3">
                        <h3 className="text-3xl font-serif text-secondary">Table Plan</h3>
                        <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-primary/5">
                            {tables.length} Tables
                        </span>
                    </div>
                </div>
                <div className="flex gap-3 items-center">
                    <Button onClick={() => { setEditingTable(null); setIsAddTableOpen(true); }} size="sm">
                        Add Table
                    </Button>
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className={`px-4 py-2 rounded-full text-sm font-bold uppercase tracking-widest transition-all border ${
                            sidebarOpen
                                ? 'bg-secondary text-white border-secondary'
                                : 'bg-white text-secondary border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        {sidebarOpen ? 'Hide' : 'Show'} Unassigned ({unassignedGuests.length})
                    </button>
                    <Link to=".." className="text-secondary-light hover:text-primary text-sm font-black uppercase tracking-widest py-2 px-2 transition-colors">
                        Back
                    </Link>
                </div>
            </div>

            {/* Stats bar */}
            <div className="flex gap-6 mb-8 flex-wrap">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-3 flex items-center gap-3">
                    <span className="text-xs font-bold text-secondary-light uppercase tracking-widest">Seated</span>
                    <span className="text-2xl font-serif text-secondary">{totalSeated}</span>
                </div>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-3 flex items-center gap-3">
                    <span className="text-xs font-bold text-secondary-light uppercase tracking-widest">Capacity</span>
                    <span className="text-2xl font-serif text-secondary">{totalCapacity}</span>
                </div>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-3 flex items-center gap-3">
                    <span className="text-xs font-bold text-secondary-light uppercase tracking-widest">Unassigned</span>
                    <span className={`text-2xl font-serif ${unassignedGuests.length > 0 ? 'text-accent' : 'text-success'}`}>
                        {unassignedGuests.length}
                    </span>
                </div>
            </div>

            {/* Assignment mode banner */}
            {assigningGuest && (
                <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-2xl flex items-center justify-between animate-fade-in">
                    <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary text-lg">↗</span>
                        <span className="text-sm font-bold text-secondary">
                            Click a table to assign <span className="text-primary">{assigningGuest._displayName}</span>
                        </span>
                    </div>
                    <button
                        onClick={() => setAssigningGuest(null)}
                        className="text-sm text-secondary-light hover:text-secondary font-bold uppercase tracking-widest transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            )}

            {/* Auto-create orphans banner */}
            {orphanedTableNames.length > 0 && (
                <div className="mb-6 p-4 bg-accent-soft border border-accent/10 rounded-2xl flex items-center justify-between">
                    <div>
                        <p className="text-sm font-bold text-secondary">
                            {orphanedTableNames.length} table name(s) found on guests without matching table records
                        </p>
                        <p className="text-xs text-secondary-light mt-1">
                            {orphanedTableNames.join(', ')}
                        </p>
                    </div>
                    <Button onClick={handleAutoCreate} size="sm" variant="ghost">
                        Auto-Create Tables
                    </Button>
                </div>
            )}

            <div className="flex gap-6">
                {/* Main floor plan */}
                <div className={`flex-1 min-w-0 transition-all duration-300 ${sidebarOpen ? '' : ''}`}>
                    {tables.length === 0 && orphanedTableNames.length === 0 ? (
                        <div className="text-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm">
                            <p className="text-4xl mb-4">🪑</p>
                            <p className="text-lg font-serif text-secondary mb-2">No tables yet</p>
                            <p className="text-sm text-secondary-light mb-6">Add your first table to start building the seating plan</p>
                            <Button onClick={() => { setEditingTable(null); setIsAddTableOpen(true); }} size="sm">
                                Add Table
                            </Button>
                        </div>
                    ) : (
                        <div className="grid gap-2" style={{
                            gridTemplateColumns: `repeat(auto-fill, minmax(280px, 1fr))`,
                        }}>
                            {tables.map(table => {
                                const tableGuests = guestsByTable[table.name] || [];
                                return (
                                    <div
                                        key={table.id}
                                        className={`bg-white/60 rounded-3xl border transition-all duration-200 p-2 ${
                                            assigningGuest
                                                ? 'border-primary/30 hover:border-primary hover:bg-primary/5 cursor-pointer'
                                                : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
                                        }`}
                                    >
                                        <TableVisual
                                            tableName={table.name}
                                            shape={table.shape}
                                            capacity={table.capacity}
                                            guests={tableGuests}
                                            isSelected={detailTable?.id === table.id}
                                            isAssignTarget={!!assigningGuest}
                                            onClick={() => handleTableClick(table)}
                                        />
                                        {/* Edit button */}
                                        <div className="text-center -mt-2 mb-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingTable(table);
                                                    setIsAddTableOpen(true);
                                                }}
                                                className="text-[10px] font-bold text-secondary-light/50 hover:text-primary uppercase tracking-widest transition-colors"
                                            >
                                                Edit Table
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Orphaned tables (exist on guests but not in tables collection) */}
                            {orphanedTableNames.map(name => {
                                const tableGuests = guestsByTable[name] || [];
                                return (
                                    <div
                                        key={`orphan-${name}`}
                                        className="bg-accent-soft/30 rounded-3xl border border-accent/10 p-2 relative"
                                    >
                                        <div className="absolute top-3 right-3">
                                            <span className="bg-accent/10 text-accent text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-accent/20">
                                                Unregistered
                                            </span>
                                        </div>
                                        <TableVisual
                                            tableName={name}
                                            shape="round"
                                            capacity={tableGuests.length}
                                            guests={tableGuests}
                                            onClick={() => {
                                                if (assigningGuest) handleAssignGuest(name);
                                            }}
                                            isAssignTarget={!!assigningGuest}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Unassigned guests sidebar */}
                {sidebarOpen && (
                    <div className="w-72 flex-shrink-0 animate-fade-in">
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm sticky top-4 max-h-[calc(100vh-120px)] flex flex-col">
                            <div className="p-4 border-b border-gray-100">
                                <h4 className="text-sm font-bold text-secondary uppercase tracking-widest mb-3">
                                    Unassigned
                                    <span className="ml-2 bg-accent/10 text-accent text-[10px] px-2 py-0.5 rounded-full">
                                        {unassignedGuests.length}
                                    </span>
                                </h4>
                                <input
                                    type="text"
                                    placeholder="Search guests..."
                                    className="w-full p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                    value={sidebarSearch}
                                    onChange={e => setSidebarSearch(e.target.value)}
                                />
                            </div>
                            <div className="flex-1 overflow-y-auto p-2">
                                {filteredUnassigned.length === 0 ? (
                                    <div className="text-center py-8 text-secondary-light text-sm">
                                        {unassignedGuests.length === 0 ? (
                                            <div>
                                                <p className="text-2xl mb-2">🎉</p>
                                                <p className="font-medium">Everyone is seated!</p>
                                            </div>
                                        ) : (
                                            'No matches'
                                        )}
                                    </div>
                                ) : (
                                    filteredUnassigned.map(g => (
                                        <button
                                            key={g.id}
                                            onClick={() => setAssigningGuest(g)}
                                            className={`w-full text-left px-3 py-2 rounded-xl transition-all text-sm flex items-center gap-2 group ${
                                                assigningGuest?.id === g.id
                                                    ? 'bg-primary/10 text-primary ring-2 ring-primary/20'
                                                    : 'hover:bg-bg-subtle text-secondary'
                                            }`}
                                        >
                                            {g._guestType === 'plusOne' ? (
                                                <span className="w-5 h-5 rounded-full bg-purple-100 border border-purple-300 flex items-center justify-center text-[8px] font-bold text-purple-700 flex-shrink-0">+1</span>
                                            ) : g._guestType === 'child' ? (
                                                <span className="w-5 h-5 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center text-[8px] font-bold text-amber-700 flex-shrink-0">C</span>
                                            ) : (
                                                <span className="w-5 h-5 rounded-full bg-primary-light/60 border border-primary/30 flex-shrink-0" />
                                            )}
                                            <span className="truncate font-medium">{g._displayName}</span>
                                            <svg className="w-3.5 h-3.5 ml-auto text-secondary-light/30 group-hover:text-primary transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add/Edit Table Modal */}
            <AddTableModal
                isOpen={isAddTableOpen}
                onClose={() => { setIsAddTableOpen(false); setEditingTable(null); }}
                onSuccess={fetchData}
                editTable={editingTable}
                nextSortOrder={tables.length}
            />

            {/* Table Detail Modal */}
            <Modal
                isOpen={!!detailTable}
                onClose={() => { setDetailTableId(null); setDetailAddSearch(''); }}
                title={detailTable?.name || 'Table Details'}
            >
                {detailTable && (
                    <div className="space-y-5">
                        {/* Capacity bar */}
                        <div>
                            <div className="flex justify-between text-sm mb-2">
                                <span className="text-secondary-light font-bold uppercase tracking-widest text-xs">Capacity</span>
                                <span className={`font-bold ${
                                    detailGuests.length > detailTable.capacity ? 'text-error' : 'text-secondary'
                                }`}>
                                    {detailGuests.length} / {detailTable.capacity}
                                </span>
                            </div>
                            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all duration-500 ${
                                        detailGuests.length > detailTable.capacity
                                            ? 'bg-error'
                                            : detailGuests.length >= detailTable.capacity
                                            ? 'bg-primary-dark'
                                            : 'bg-primary'
                                    }`}
                                    style={{ width: `${Math.min(100, (detailGuests.length / detailTable.capacity) * 100)}%` }}
                                />
                            </div>
                        </div>

                        {/* Seated guest list with reorder */}
                        <div>
                            <p className="text-xs font-bold text-secondary-light uppercase tracking-widest mb-2">Seating Order</p>
                            {detailGuests.length === 0 ? (
                                <p className="text-sm text-secondary-light text-center py-4">No guests assigned yet</p>
                            ) : (
                                <div className="space-y-0.5">
                                    {(() => {
                                        // Build ordered list of primary guests (with their party grouped)
                                        const primaryIds: string[] = [];
                                        const seen = new Set<string>();
                                        for (const g of detailGuests) {
                                            const pid = (g as any)._parentId || g.id;
                                            if (!seen.has(pid)) {
                                                seen.add(pid);
                                                primaryIds.push(pid);
                                            }
                                        }

                                        return primaryIds.map((pid, pidIdx) => {
                                            // Get all guests for this primary
                                            const partyGuests = detailGuests.filter(g => {
                                                const gPid = (g as any)._parentId || g.id;
                                                return gPid === pid;
                                            });

                                            return (
                                                <div
                                                    key={pid}
                                                    draggable
                                                    onDragStart={(e) => {
                                                        setDraggingPid(pid);
                                                        e.dataTransfer.effectAllowed = 'move';
                                                        e.dataTransfer.setData('text/plain', pid);
                                                    }}
                                                    onDragEnd={() => {
                                                        setDraggingPid(null);
                                                        setDragOverPid(null);
                                                    }}
                                                    onDragOver={(e) => {
                                                        e.preventDefault();
                                                        e.dataTransfer.dropEffect = 'move';
                                                        if (draggingPid && draggingPid !== pid) {
                                                            setDragOverPid(pid);
                                                        }
                                                    }}
                                                    onDragLeave={() => {
                                                        if (dragOverPid === pid) setDragOverPid(null);
                                                    }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        const fromPid = e.dataTransfer.getData('text/plain');
                                                        if (fromPid && fromPid !== pid) {
                                                            handleDragReorder(detailTable.id, fromPid, pid);
                                                        }
                                                        setDraggingPid(null);
                                                        setDragOverPid(null);
                                                    }}
                                                    className={`rounded-xl border bg-white overflow-hidden transition-all duration-150 ${
                                                        draggingPid === pid
                                                            ? 'opacity-40 scale-95 border-gray-200'
                                                            : dragOverPid === pid
                                                            ? 'border-primary ring-2 ring-primary/20 scale-[1.01]'
                                                            : 'border-gray-100'
                                                    }`}
                                                >
                                                    {partyGuests.map((g, gIdx) => {
                                                        const isPrimary = g._guestType === 'primary';
                                                        return (
                                                            <div
                                                                key={g.id}
                                                                className={`flex items-center gap-2 px-3 py-2.5 group transition-colors hover:bg-bg-subtle ${
                                                                    gIdx > 0 ? 'border-t border-gray-50 pl-8' : ''
                                                                }`}
                                                            >
                                                                {/* Drag handle + seat number for primary */}
                                                                {isPrimary ? (
                                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                                        <span className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-secondary-light transition-colors -ml-1" title="Drag to reorder">
                                                                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                                                                <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" />
                                                                                <circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" />
                                                                                <circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
                                                                            </svg>
                                                                        </span>
                                                                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-secondary-light">
                                                                            {pidIdx + 1}
                                                                        </span>
                                                                    </div>
                                                                ) : null}

                                                                {/* Type badge */}
                                                                {g._guestType === 'plusOne' ? (
                                                                    <span className="w-5 h-5 rounded-full bg-purple-100 border border-purple-300 flex items-center justify-center text-[8px] font-bold text-purple-700 flex-shrink-0">+1</span>
                                                                ) : g._guestType === 'child' ? (
                                                                    <span className="w-5 h-5 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center text-[8px] font-bold text-amber-700 flex-shrink-0">C</span>
                                                                ) : (
                                                                    <span className="w-5 h-5 rounded-full bg-primary-light/60 border border-primary/30 flex-shrink-0" />
                                                                )}

                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium text-secondary truncate">{g._displayName}</p>
                                                                    {getDietaryDisplay(g) && (
                                                                        <p className="text-[10px] text-secondary-light mt-0.5 truncate">🍽 {getDietaryDisplay(g)}</p>
                                                                    )}
                                                                </div>

                                                                {/* Reorder + remove — only on primary */}
                                                                {isPrimary && (
                                                                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                        <button
                                                                            onClick={() => handleReorderGuest(detailTable.id, pid, 'up')}
                                                                            disabled={pidIdx === 0}
                                                                            className="p-1 rounded hover:bg-gray-100 text-secondary-light hover:text-secondary disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                                                            title="Move up"
                                                                        >
                                                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
                                                                            </svg>
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleReorderGuest(detailTable.id, pid, 'down')}
                                                                            disabled={pidIdx === primaryIds.length - 1}
                                                                            className="p-1 rounded hover:bg-gray-100 text-secondary-light hover:text-secondary disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                                                            title="Move down"
                                                                        >
                                                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                                                            </svg>
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleRemoveFromTable(pid)}
                                                                            className="p-1 rounded hover:bg-error/10 text-secondary-light hover:text-error transition-colors ml-1"
                                                                            title="Remove from table"
                                                                        >
                                                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            )}
                        </div>

                        {/* Add guest to table */}
                        <div className="border-t border-gray-100 pt-4">
                            <p className="text-xs font-bold text-secondary-light uppercase tracking-widest mb-2">Add Guest</p>
                            <input
                                type="text"
                                placeholder="Search unassigned guests..."
                                className="w-full p-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                value={detailAddSearch}
                                onChange={e => setDetailAddSearch(e.target.value)}
                            />
                            {detailAddSearch && (
                                <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5">
                                    {detailAddCandidates.length === 0 ? (
                                        <p className="text-xs text-secondary-light text-center py-3">No matching unassigned guests</p>
                                    ) : (
                                        detailAddCandidates.map(g => (
                                            <button
                                                key={g.id}
                                                onClick={async () => {
                                                    await handleAssignGuest(detailTable.name, g);
                                                    setDetailAddSearch('');
                                                }}
                                                className="w-full text-left px-3 py-2 rounded-lg hover:bg-primary/5 transition-colors flex items-center gap-2 text-sm group"
                                            >
                                                <span className="w-5 h-5 rounded-full bg-primary-light/60 border border-primary/30 flex-shrink-0" />
                                                <span className="truncate font-medium text-secondary">{g._displayName}</span>
                                                <svg className="w-4 h-4 ml-auto text-primary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-2 border-t border-gray-100">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setDetailTableId(null);
                                    setDetailAddSearch('');
                                    setEditingTable(detailTable);
                                    setIsAddTableOpen(true);
                                }}
                                className="flex-1"
                            >
                                Edit Table Settings
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

function getDietaryDisplay(g: GuestRow): string {
    if (!g.dietary) return '';
    if (typeof g.dietary === 'string') {
        return g._guestType === 'primary' ? g.dietary : '';
    }
    if (g._guestType === 'plusOne') return g.dietary.plusOne || '';
    if (g._guestType === 'child') {
        const children = g.dietary.children || [];
        const idx = (g as any)._childIndex || 0;
        return children[idx] || '';
    }
    return g.dietary.guest || '';
}
